// @flow
import React, { Component } from "react";
import classNames from "classnames";
import withStyles from "elevate-ui/withStyles";
import uniq from "lodash/uniq";
import compact from "lodash/compact";
import reduce from "lodash/reduce";
import { FastField } from "formik";
import Input from "elevate-ui/Input";
import Select from "elevate-ui/Select";
import MultiSelect from "elevate-ui/MultiSelect";
import ButtonGroup from "elevate-ui/ButtonGroup";
import Datetime from "elevate-ui/Datetime";
import FormSectionHeading from "../common/FormSectionHeading";

type $Props = {
  advancedSearchFilterOrder: Object,
  classes: Object,
  className: string,
  currentPropertyTypes: Array<string>,
  handleSearch: Function,
  prepopulated: Object,
  query: Object,
  sectionFilters: Array<Object>,
  title: string,
  visible: boolean,
  mlsApiVersion: number,
};

class FilterSection extends Component<$Props> {
  section: any;

  renderFilters({ sectionKey, sectionFilters, currentPropertyTypes }) {
    const { advancedSearchFilterOrder, classes } = this.props;
    if (!advancedSearchFilterOrder[sectionKey]) {
      return null;
    }
    return compact(
      advancedSearchFilterOrder[sectionKey].map((filterKey, i) => {
        const filter = sectionFilters[filterKey];
        if (!filter || (filter && filter.crmOnly)) return null;
        let options = filter.options || [];
        switch (filter.type) {
          case "text":
            if (filter.isVisible !== undefined && filter.isVisible === false) {
              return null;
            }
            if (!filter.createRangeInputs) {
              return (
                <FastField
                  key={i}
                  id={filter.name}
                  name={filter.name}
                  label={filter.label || filter.name}
                  component={Input}
                />
              );
            }
            return (
              <div className={classes.inputGroup} key={i}>
                <FastField
                  id={`min${filter.name}`}
                  name={
                    this.props.mlsApiVersion === 1
                      ? `min${filter.name}`
                      : `amin_${filter.name}`
                  }
                  label={`Min ${filter.label}`}
                  placeholder={filter.name === "livingArea" ? "Sqft" : null}
                  component={Input}
                />
                <FastField
                  id={`max${filter.name}`}
                  name={
                    this.props.mlsApiVersion === 1
                      ? `max${filter.name}`
                      : `amax_${filter.name}`
                  }
                  label={`Max ${filter.label}`}
                  placeholder={filter.name === "livingArea" ? "Sqft" : null}
                  component={Input}
                />
              </div>
            );
          case "dateTime":
            if (!filter.createRangeInputs) {
              return (
                <FastField
                  style={{ margin: "0" }}
                  key={i}
                  id={filter.name}
                  name={filter.name}
                  label={filter.label || filter.name}
                  component={Datetime}
                  dateFormat="MMMM do h:mm aa"
                  showTimeSelect
                  popperPlacement="bottom-start"
                  showPopperArrow={false}
                />
              );
            }
            return (
              <div className={classes.inputGroup} key={i}>
                <FastField
                  style={{ margin: "0" }}
                  id={`min${filter.name}`}
                  name={
                    this.props.mlsApiVersion === 1
                      ? `min${filter.name}`
                      : `amin_${filter.name}`
                  }
                  label={`${filter.label} Start`}
                  component={Datetime}
                  dateFormat="MMMM do h:mm aa"
                  showTimeSelect
                  popperPlacement="bottom-start"
                  showPopperArrow={false}
                />
                <FastField
                  style={{ margin: "0" }}
                  id={`max${filter.name}`}
                  name={
                    this.props.mlsApiVersion === 1
                      ? `max${filter.name}`
                      : `amax_${filter.name}`
                  }
                  label={`${filter.label} End`}
                  component={Datetime}
                  dateFormat="MMMM do h:mm aa"
                  showTimeSelect
                  popperPlacement="bottom-end"
                  showPopperArrow={false}
                />
              </div>
            );
          case "buttongroup":
            return options && options.length ? (
              <FastField
                key={i}
                id={filter.name}
                name={filter.name}
                label={filter.label || filter.name}
                component={ButtonGroup}
                className={classes.smallButtonGroup}
                items={options}
              />
            ) : null;
          case "multiselect":
            if (
              filter.propertyTypes &&
              typeof filter.propertyTypes === "object"
            ) {
              if (filter.requiresPropertyType) {
                if (currentPropertyTypes && currentPropertyTypes.length) {
                  options = uniq(
                    reduce(
                      currentPropertyTypes,
                      (memo, currentPropertyType) => {
                        if (
                          currentPropertyType &&
                          filter.propertyTypes[currentPropertyType]
                        ) {
                          memo = memo.concat(
                            filter.propertyTypes[currentPropertyType]
                          );
                        }
                        return memo;
                      },
                      []
                    )
                  ).map((o) => ({ label: o, value: o }));
                } else {
                  options = uniq(
                    reduce(
                      filter.propertyTypes,
                      (key, values) => {
                        return values;
                      },
                      []
                    )
                  ).map((o) => ({ label: o, value: o }));
                }
              } else if (!filter.requiresPropertyType) {
                // Show all possible values, useful for fields like `City` where we want to always show them
                options = uniq(
                  reduce(
                    filter.propertyTypes,
                    (memo, values) => {
                      values.map((o) => {
                        memo.push(o);
                        return true;
                      });
                      return memo;
                    },
                    []
                  )
                ).map((o) => ({ label: o, value: o }));
              }
            }

            return options && options.length ? (
              <FastField
                key={i}
                id={filter.name}
                name={filter.name}
                label={filter.label || filter.name}
                component={MultiSelect}
                items={options}
                tags
              />
            ) : null;
          case "select":
            if (options && options.length) {
              if (!filter.createRangeInputs) {
                return (
                  <FastField
                    key={i}
                    id={filter.name}
                    name={filter.name}
                    label={filter.label || filter.name}
                    component={Select}
                    items={options}
                  />
                );
              }
              return (
                <div className={classes.inputGroup} key={i}>
                  <FastField
                    id={`min${filter.name}`}
                    name={`min${filter.name}`}
                    label={`Min ${filter.label}`}
                    component={Select}
                    items={options}
                  />
                  <FastField
                    id={`max${filter.name}`}
                    name={`max${filter.name}`}
                    label={`Max ${filter.label}`}
                    component={Select}
                    items={options}
                  />
                </div>
              );
            }
            return null;
          default:
            console.error(`Advanced filter: ${filter.name} has invalid config`); // eslint-disable-line
            return null;
        }
      })
    );
  }

  render() {
    const {
      classes,
      className,
      currentPropertyTypes,
      handleSearch,
      prepopulated,
      query,
      sectionFilters,
      title,
      visible,
    }: $Props = this.props;

    const filters = this.renderFilters({
      sectionKey: title,
      sectionFilters,
      query,
      prepopulated,
      handleSearch,
      currentPropertyTypes,
    });

    // Did our filters have values and render or not?
    if (!(filters && filters.length)) {
      return null;
    }

    return (
      <div
        id="findme"
        className={classNames(
          classes.root,
          !visible && classes.hidden,
          className
        )}
        ref={(node) => (this.section = node)}
      >
        <FormSectionHeading title={title} />
        <div className={classes.grid}>{filters}</div>
      </div>
    );
  }
}

export default withStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  hidden: {
    display: "none",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(1, minmax(0, 1fr))",
    gridColumnGap: "12px",
    gridRowGap: "16px",

    [theme.breakpoints(600)]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },

    [theme.breakpoints(900)]: {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    },
  },
  inputGroup: {
    display: "grid",
    gridTemplateColumns: "repeat(1, minmax(0, 1fr))",
    gridColumnGap: "16px", // smaller space than regular grid
    gridRowGap: "16px",

    [theme.breakpoints(600)]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  smallButtonGroup: { "& label": { padding: "12px" } },
}))(FilterSection);
